/**
 * Philosophy Section Component
 * Explains OKR Hub's philosophy and approach
 */

import React from 'react';
import { Section } from './Section';
import { SectionHeader } from './SectionHeader';
import { X, CheckCircle } from 'lucide-react';

const notOKRs = [
  'OKRs are not performance appraisals',
  'OKRs are not task trackers',
  'OKRs are not quarterly compliance rituals'
];

const whatOKRsAre = [
  'Create shared clarity',
  'Enable accountability without micromanagement',
  'Improve decision-making quality',
  'Build execution discipline through rhythm and review'
];

export const PhilosophySection: React.FC = () => {
  return (
    <Section id="philosophy" background="sand" padding="xl">
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          title="Our Philosophy: OKRs Are Not a Scorecard"
          description="At OKR Hub, we believe:"
          align="center"
        />

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* What OKRs Are NOT */}
          <div className="bg-white rounded-2xl p-8 border-2 border-stone-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-error-100 rounded-xl flex items-center justify-center">
                <X className="w-6 h-6 text-error-600" />
              </div>
              <h3 className="font-display text-2xl text-ink">OKRs Are Not</h3>
            </div>

            <ul className="space-y-4">
              {notOKRs.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-error-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-4 h-4 text-error-600" />
                  </div>
                  <span className="text-ink-light text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What OKRs ARE */}
          <div className="bg-white rounded-2xl p-8 border-2 border-primary-300 shadow-soft-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-success-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
              <h3 className="font-display text-2xl text-ink">OKRs Are a Way To</h3>
            </div>

            <ul className="space-y-4">
              {whatOKRsAre.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-success-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-ink-light text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Closing Statement */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <p className="text-2xl md:text-3xl font-display leading-relaxed">
            "Everything we offer—consulting, managed services, and software—is designed around this belief."
          </p>
        </div>
      </div>
    </Section>
  );
};
